import { Paper, PaperProps } from '@material-ui/core';
import { classnames } from '@material-ui/data-grid';
import { Autocomplete as Component } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { TestId } from '../Testing/TestId';
import TextField, { TextFieldProps } from './text-field';

const useStyles = makeStyles({
  disabledSearch: {
    '& .MuiAutocomplete-inputRoot, & input': {
      cursor: 'pointer',
    },
  },
  activeSearch: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2575FF !important',
      borderWidth: '1px !important',
    },
  },
  paper: {
    padding: 0,
    borderRadius: 4,
    boxShadow:
      '0px 0px 2px rgba(9, 29, 61, 0.25), 0px 8px 16px rgba(34, 91, 187, 0.08), 0px 8px 24px rgba(51, 126, 255, 0.08)',
  },
});

export interface Option<T> {
  value: T;
  label: string;
}

export interface AutocompleteProps<T, M extends undefined | boolean> {
  value?: M extends undefined | false ? T : T[];
  options: Option<T>[];
  disabled?: boolean;
  textFieldProps?: TextFieldProps;
  onChange: (value: M extends undefined | false ? T : T[]) => void;
  readOnly?: boolean;
  disableSearch?: boolean;
  multiple: M;
  className?: string;
  testId?: string;
  size?: 'small' | 'medium';
  isActive?: boolean;
  defaultInputValue?: string;
}

export function Autocomplete<T, M extends boolean>({
  className,
  multiple,
  value,
  options,
  onChange,
  readOnly,
  disabled,
  testId,
  textFieldProps,
  size,
  disableSearch,
  isActive = false,
  defaultInputValue = '',
}: AutocompleteProps<T, M>) {
  const classes = useStyles();

  const [inputValue, setInputValue] = useState<string>(defaultInputValue);

  useEffect(() => {
    if (defaultInputValue && !inputValue) {
      setInputValue(defaultInputValue);
    }
  }, [defaultInputValue, inputValue]);

  useEffect(() => {
    if (!value && !!inputValue) {
      setInputValue(defaultInputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Component<T, M, true>
      className={classnames(className, {
        teaT: true,
        [classes.disabledSearch]: disableSearch,
        [classes.activeSearch]: isActive,
      })}
      size={size}
      multiple={multiple}
      // @ts-expect-error
      value={value}
      disableCloseOnSelect={multiple}
      options={options.map(({ value }) => value)}
      disableClearable
      getOptionLabel={(value) =>
        options.find((v) => v.value === value)?.label ?? ''
      }
      onChange={(e, v) => {
        onChange(v);
        setInputValue('');
      }}
      filterSelectedOptions
      data-testid={testId}
      selectOnFocus={!disableSearch}
      forcePopupIcon={readOnly ? false : undefined}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...textFieldProps}
            {...params}
            inputProps={{
              ...(params?.inputProps || {}),
              readOnly: readOnly || disableSearch,
            }}
          />
        );
      }}
      PaperComponent={(paperProps: PaperProps) => {
        return !readOnly ? (
          <TestId testId={`${testId}-dropdown`}>
            <Paper {...paperProps} className={classes.paper} />
          </TestId>
        ) : (
          <></>
        );
      }}
      disabled={disabled}
    />
  );
}
